import { ReactNode, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  IconDefinition,
  faBookOpen,
  faCalendarCheck,
  faCheck,
  faEnvelope,
  faFont,
  faGlobe,
  faLock,
  faMobileScreen,
  faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { UserPatchType, VENUE_NAME_MAX_LENGTH, validateVenueName } from '@meniudigital/shared'
import { Backdrop, Button, CloseButton } from 'src/global'
import { State } from 'src/state'
import { patchCurrentUser } from 'src/state/users'
import { IS_DESKTOP_OR_TABLET } from '..'
import Setting from '../SettingsModal/Setting'
import { RoundIconWrapper } from '../SettingsModal/BasicSettings/styles'
import { Modal, Header, ModalContent, ModalFooter } from './styles'

const TEST_WORD_REGEX = /\btest\b/i
// SRL and PFA with or without dots; S.A. and I.I. only dotted, since "Sa" and a Roman numeral II are real name parts
const COMPANY_SUFFIX_REGEX = /\b(s\.?r\.?l|p\.?f\.?a|s\.a|i\.i)\b/i

type Tip = { icon: IconDefinition; text: string }

const RECOMMENDATIONS: Tip[] = [
  { icon: faUsers, text: 'Folosește numele sub care te cunosc clienții, nu denumirea firmei (SRL, PFA).' },
  { icon: faFont, text: 'Scrie-l cu majusculă la început, de exemplu Pizzeria Maria, nu PIZZERIA MARIA.' },
  { icon: faMobileScreen, text: 'Păstrează-l scurt: numele lungi se afișează mai mic pe telefon.' },
]

const WHERE_IT_SHOWS: Tip[] = [
  { icon: faBookOpen, text: 'În partea de sus a meniului, lângă logo.' },
  { icon: faGlobe, text: 'În titlul paginii și în rezultatele Google.' },
  { icon: faEnvelope, text: 'În emailurile de confirmare a comenzilor.' },
  { icon: faCalendarCheck, text: 'Pe pagina de rezervări și în widgetul de comenzi.' },
]

export default function ChangeNameModal() {
  const dispatch = useDispatch<ThunkDispatch<State, undefined, AnyAction>>()
  const { user } = useSelector((state: State) => state.users)

  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    window.openChangeNameModal = () => setIsOpen(true)
  }, [])

  useEffect(() => {
    if (isOpen) {
      setName(user?.name || '')
      if (IS_DESKTOP_OR_TABLET) {
        inputRef.current?.focus()
      }
    }
  }, [isOpen])

  if (!user) {
    return null
  }

  const trimmedName = name.trim()
  const currentName = (user.name || '').trim()
  const isUnchanged = trimmedName === currentName

  const blockingError = isUnchanged
    ? undefined
    : validateVenueName(name) ||
      (TEST_WORD_REGEX.test(trimmedName)
        ? 'Numele localului nu poate conține cuvântul „test”. Introdu numele real al localului tău.'
        : undefined)

  const hasLetters = /[a-zăâîșț]/i.test(trimmedName)
  const warnings = [
    hasLetters && trimmedName.length >= 4 && trimmedName === trimmedName.toUpperCase()
      ? 'Numele e scris doar cu majuscule. În meniu arată mai bine scris normal, de exemplu Pizzeria Maria.'
      : undefined,
    COMPANY_SUFFIX_REGEX.test(trimmedName)
      ? 'Pare să fie denumirea firmei. Clienții caută numele localului, nu al societății.'
      : undefined,
  ].filter(Boolean) as string[]

  const canSave = !isUnchanged && !blockingError && !isConfirming && !isSaving

  // The strip under the name card reflects the input: error, soft warning, or what is about to happen
  let explainerVariant: 'warning' | 'error' | undefined
  let explainerText: ReactNode
  if (blockingError) {
    explainerVariant = 'error'
    explainerText = blockingError
  } else if (warnings.length) {
    explainerVariant = 'warning'
    explainerText = warnings.join(' ')
  } else if (isUnchanged) {
    explainerText = (
      <>
        Numele curent: <b>{currentName}</b>.
      </>
    )
  } else {
    explainerText = (
      <>
        Numele se schimbă din <b>{currentName}</b> în <b>{trimmedName}</b>.
      </>
    )
  }

  const close = () => {
    setIsOpen(false)
  }

  const save = async () => {
    if (!canSave) {
      return
    }

    // Yes/no dialogs go through showModal; showConfirm is the info-only variant without a cancel button
    setIsConfirming(true)
    try {
      await window.showModal({
        title: 'Confirmi noul nume?',
        textComponent: (
          <span>
            Numele meniului se schimbă din <b>{currentName}</b> în <b>{trimmedName}</b>.
            <br />
            Username-ul rămâne <b>{user.username}</b>, iar linkul și codul QR rămân neschimbate.
          </span>
        ),
        confirmButtonText: 'Da, schimbă numele',
        cancelButtonText: 'Anulează',
      })
    } catch {
      return
    } finally {
      setIsConfirming(false)
    }

    setIsSaving(true)
    const result = await dispatch(patchCurrentUser({ type: UserPatchType.Name, data: trimmedName }))
    setIsSaving(false)

    if (patchCurrentUser.fulfilled.match(result)) {
      close()
    }
  }

  const renderTips = (tips: Tip[]) => (
    <div className="settings-group plain">
      {tips.map(tip => (
        <Setting
          key={tip.text}
          title={tip.text}
          rightButton={null}
          icon={
            <div className="row-icon">
              <FontAwesomeIcon icon={tip.icon} />
            </div>
          }
        />
      ))}
    </div>
  )

  // Opened on top of the settings modal, which stays visible underneath (same layering as the product picker).
  // The content mounts only while open, so a closed modal leaves nothing in the Tab order
  return (
    <>
      <Backdrop onClick={close} className={isOpen ? 'open' : 'closed'} style={{ zIndex: 100 }} />
      <Modal className={isOpen ? 'open' : 'closed'}>
        <Header>
          Modifică numele restaurantului
          <CloseButton onClick={close} title="Închide" style={{ marginTop: '-18px' }} />
        </Header>
        {isOpen && (
          <>
            <ModalContent>
              <RoundIconWrapper>
                <Setting
                  title="Numele Restaurantului / Meniului"
                  icon={user.logoUrl && <img className="logo" src={user.logoUrl} alt="Logo" />}
                  iconPath={user.logoUrl ? undefined : '/icons/pencil.png'}
                  rightButton={null}
                  alwaysShowsBody
                  body={
                    <div className="input-row">
                      <input
                        ref={inputRef}
                        type="text"
                        aria-label="Numele restaurantului"
                        value={name}
                        maxLength={VENUE_NAME_MAX_LENGTH}
                        placeholder="e.g. Pizzeria Maria, Domino Bistro, Caffe Costa"
                        className={blockingError ? 'invalid' : ''}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && save()}
                      />
                      <span className={`counter ${name.length >= VENUE_NAME_MAX_LENGTH ? 'at-limit' : ''}`}>
                        {name.length}/{VENUE_NAME_MAX_LENGTH}
                      </span>
                    </div>
                  }
                  infoText={explainerText}
                  infoVariant={explainerVariant}
                />
              </RoundIconWrapper>

              <Setting
                title="Username-ul tău"
                iconPath="/icons/id-card.png"
                rightButton={
                  <span className="account-id">
                    <FontAwesomeIcon icon={faLock} />
                    <span className="hidden-phone">meniudigital.ro/</span>
                    <b>{user.username}</b>
                  </span>
                }
                infoText={
                  <>
                    Username-ul nu se schimbă odată cu numele.
                    <br />
                    Dacă ești de acord că o schimbare de username invalidează codurile QR deja tipărite și vrei să le retipărești,
                    scrie-ne la <a href="mailto:contact@meniudigital.ro">contact@meniudigital.ro</a> pentru a modifica
                    username-ul.
                  </>
                }
                infoVariant="warning"
              />

              <div className="group-title">Recomandări</div>
              {renderTips(RECOMMENDATIONS)}

              <div className="group-title">Unde apare numele</div>
              {renderTips(WHERE_IT_SHOWS)}
            </ModalContent>
            <ModalFooter>
              <Button variant="as-text" onClick={close} text="Închide" />
              <Button disabled={!canSave} isLoading={isSaving} onClick={save} text="Salvează" icon={faCheck} />
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  )
}
